/**
 * AdminAPI Service
 * Gerencia todas as requisições para a API administrativa do PersonalNutri
 * Baseado em: Docs/API/ADMIN_WEB_GUIA_COMPLETO.md
 */

const API_BASE = 'https://api.personalnutri.app';

class AdminAPI {
  constructor() {
    this.token = localStorage.getItem('admin_token');
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('admin_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('admin_token');
  }

  getToken() {
    return this.token || localStorage.getItem('admin_token');
  }

  async request(endpoint, options = {}) {
    const token = this.getToken();
    
    let response;
    try {
      response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...options.headers
        }
      });
    } catch (fetchError) {
      // Erro de rede/CORS/API offline
      console.error('Erro de conexão:', fetchError);
      throw new Error('Erro de conexão com o servidor. Verifique sua internet ou tente novamente.');
    }

    // Token expirado ou inválido
    if (response.status === 401) {
      this.clearToken();
      window.location.href = '/admin/login';
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    // Sem permissão
    if (response.status === 403) {
      throw new Error('Você não tem permissão para esta ação.');
    }

    let data;
    try {
      const text = await response.text();
      console.log('Resposta raw:', text.substring(0, 500)); // Debug
      data = JSON.parse(text);
    } catch (jsonError) {
      console.error('Erro ao parsear resposta:', jsonError);
      throw new Error('Resposta inválida do servidor.');
    }

    if (!response.ok) {
      throw new Error(data.message || 'Erro na requisição');
    }

    return data;
  }

  // ==================== AUTH ====================

  /**
   * Login do usuário (mesmo endpoint de usuários normais)
   */
  async login(email, password) {
    const loginData = await this.request('/user/login', {
      method: 'POST',
      body: JSON.stringify({
        login_type: 'email',
        username: email,
        password
      })
    });

    if (loginData.status !== 'success') {
      throw new Error(loginData.message || 'Erro ao fazer login');
    }

    this.setToken(loginData.token);
    return loginData;
  }

  /**
   * Verifica se o usuário logado é admin e retorna seus dados
   */
  async getMe() {
    return this.request('/admin/me');
  }

  /**
   * Logout - limpa token local
   */
  logout() {
    this.clearToken();
  }

  // ==================== INFLUENCERS ====================

  /**
   * Lista influenciadores com paginação e filtros
   */
  async getInfluencers(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/admin/influencers${query ? `?${query}` : ''}`);
  }

  /**
   * Obtém detalhes de um influenciador
   */
  async getInfluencer(id) {
    return this.request(`/admin/influencers/${id}`);
  }

  /**
   * Cria um novo influenciador
   */
  async createInfluencer(data) {
    return this.request('/admin/influencers', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Atualiza um influenciador
   */
  async updateInfluencer(id, data) {
    return this.request(`/admin/influencers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  /**
   * Desativa ou exclui um influenciador
   * @param {boolean} hardDelete - Se true, exclui permanentemente (apenas super_admin)
   */
  async deleteInfluencer(id, hardDelete = false) {
    const query = hardDelete ? '?hard_delete=true' : '';
    return this.request(`/admin/influencers/${id}${query}`, {
      method: 'DELETE'
    });
  }

  /**
   * Obtém estatísticas de um influenciador
   */
  async getInfluencerStats(id) {
    return this.request(`/admin/influencers/${id}/stats`);
  }

  // ==================== COUPONS ====================

  /**
   * Lista cupons com paginação e filtros
   */
  async getCoupons(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/admin/coupons${query ? `?${query}` : ''}`);
  }

  /**
   * Obtém detalhes de um cupom
   */
  async getCoupon(id) {
    return this.request(`/admin/coupons/${id}`);
  }

  /**
   * Cria um novo cupom
   */
  async createCoupon(data) {
    return this.request('/admin/coupons', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Atualiza um cupom
   */
  async updateCoupon(id, data) {
    return this.request(`/admin/coupons/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  /**
   * Desativa ou exclui um cupom
   * @param {boolean} hardDelete - Se true, exclui permanentemente (apenas super_admin)
   */
  async deleteCoupon(id, hardDelete = false) {
    const query = hardDelete ? '?hard_delete=true' : '';
    return this.request(`/admin/coupons/${id}${query}`, {
      method: 'DELETE'
    });
  }

  /**
   * Obtém estatísticas de um cupom
   */
  async getCouponStats(id) {
    return this.request(`/admin/coupons/${id}/stats`);
  }

  // ==================== COMMISSIONS ====================

  /**
   * Lista comissões com paginação e filtros
   */
  async getCommissions(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/admin/commissions${query ? `?${query}` : ''}`);
  }

  /**
   * Obtém dashboard de comissões
   */
  async getCommissionsDashboard() {
    return this.request('/admin/commissions/dashboard');
  }

  /**
   * Marca uma comissão como paga
   */
  async payCommission(id, data = {}) {
    return this.request(`/admin/commissions/${id}/pay`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Pagamento em lote de comissões
   */
  async bulkPayCommissions(ids, data = {}) {
    return this.request('/admin/commissions/bulk-pay', {
      method: 'POST',
      body: JSON.stringify({ commission_ids: ids, ...data })
    });
  }

  /**
   * Lista comissões de um influenciador específico
   */
  async getInfluencerCommissions(influencerId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/admin/commissions/influencer/${influencerId}${query ? `?${query}` : ''}`);
  }

  // ==================== USERS ====================

  /**
   * Lista usuários da plataforma com paginação e filtros
   */
  async getUsers(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/admin/users${query ? `?${query}` : ''}`);
  }

  /**
   * Obtém detalhes de um usuário
   */
  async getUser(id) {
    return this.request(`/admin/users/${id}`);
  }

  /**
   * Obtém assinatura de um usuário
   */
  async getUserSubscription(id) {
    return this.request(`/admin/users/${id}/subscription`);
  }

  /**
   * Obtém estatísticas gerais de usuários
   */
  async getUsersStats() {
    return this.request('/admin/users/stats/overview');
  }

  // ==================== ADMINS (super_admin only) ====================

  /**
   * Lista administradores
   */
  async getAdmins(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/admin/admins${query ? `?${query}` : ''}`);
  }

  /**
   * Cria um novo administrador
   */
  async createAdmin(data) {
    return this.request('/admin/admins', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Atualiza um administrador
   */
  async updateAdmin(id, data) {
    return this.request(`/admin/admins/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  /**
   * Remove um administrador
   */
  async deleteAdmin(id) {
    return this.request(`/admin/admins/${id}`, {
      method: 'DELETE'
    });
  }

  // ==================== USERS TEMP (Cadastros Incompletos) ====================

  /**
   * Lista cadastros incompletos com paginação e filtros
   */
  async getUsersTemp(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/admin/users-temp${query ? `?${query}` : ''}`);
  }

  /**
   * Obtém estatísticas de cadastros incompletos por etapa
   */
  async getUsersTempStats() {
    return this.request('/admin/users-temp/stats');
  }

  /**
   * Obtém detalhes de um cadastro incompleto
   */
  async getUserTemp(id) {
    return this.request(`/admin/users-temp/${id}`);
  }

  // ==================== SUPPORT (Tickets) ====================

  /**
   * Lista tickets de suporte com paginação e filtros
   */
  async getSupportTickets(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/admin/support/tickets${query ? `?${query}` : ''}`);
  }

  /**
   * Obtém detalhes de um ticket com mensagens
   */
  async getSupportTicket(id) {
    return this.request(`/admin/support/tickets/${id}`);
  }

  /**
   * Responde a um ticket
   */
  async replySupportTicket(id, mensagem) {
    return this.request(`/admin/support/tickets/${id}/reply`, {
      method: 'POST',
      body: JSON.stringify({ mensagem })
    });
  }

  /**
   * Atualiza status, prioridade ou atribuição de um ticket
   */
  async updateSupportTicket(id, data) {
    return this.request(`/admin/support/tickets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  /**
   * Obtém dashboard de suporte com métricas de SLA
   */
  async getSupportDashboard() {
    return this.request('/admin/support/dashboard');
  }

  // ==================== MESSAGE LOGS (Email/SMS) ====================

  /**
   * Lista logs de mensagens com paginação e filtros
   */
  async getMessageLogs(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/admin/message-logs${query ? `?${query}` : ''}`);
  }

  /**
   * Obtém estatísticas de envio de mensagens
   * @param {number} days - Número de dias para estatísticas (default: 7, max: 90)
   */
  async getMessageLogsStats(days = 7) {
    return this.request(`/admin/message-logs/stats?days=${days}`);
  }

  /**
   * Obtém detalhes de um log específico
   */
  async getMessageLogDetail(id) {
    return this.request(`/admin/message-logs/${id}`);
  }

  /**
   * Reenvia uma mensagem
   * @param {number} id - ID do log de mensagem
   */
  async resendMessage(id) {
    return this.request(`/admin/message-logs/${id}/resend`, {
      method: 'POST'
    });
  }

  // ==================== INGREDIENT CATEGORIES ====================

  /**
   * Lista categorias de ingredientes
   */
  async getIngredientCategories(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/admin/ingredient-categories${query ? `?${query}` : ''}`);
  }

  /**
   * Obtém detalhes de uma categoria
   */
  async getIngredientCategory(id) {
    return this.request(`/admin/ingredient-categories/${id}`);
  }

  /**
   * Cria uma nova categoria de ingredientes
   */
  async createIngredientCategory(data) {
    return this.request('/admin/ingredient-categories', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Atualiza uma categoria de ingredientes
   */
  async updateIngredientCategory(id, data) {
    return this.request(`/admin/ingredient-categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  /**
   * Exclui uma categoria de ingredientes
   */
  async deleteIngredientCategory(id) {
    return this.request(`/admin/ingredient-categories/${id}`, {
      method: 'DELETE'
    });
  }

  // ==================== INGREDIENTS (Catálogo Global) ====================

  /**
   * Lista ingredientes do catálogo global
   */
  async getIngredients(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/admin/ingredients${query ? `?${query}` : ''}`);
  }

  /**
   * Obtém detalhes de um ingrediente
   */
  async getIngredient(id) {
    return this.request(`/admin/ingredients/${id}`);
  }

  /**
   * Cria um novo ingrediente
   */
  async createIngredient(data) {
    return this.request('/admin/ingredients', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Atualiza um ingrediente
   */
  async updateIngredient(id, data) {
    return this.request(`/admin/ingredients/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  /**
   * Exclui um ingrediente
   */
  async deleteIngredient(id) {
    return this.request(`/admin/ingredients/${id}`, {
      method: 'DELETE'
    });
  }

  // ==================== USER MEALS (Refeições de Usuários) ====================

  /**
   * Lista refeições de um usuário
   */
  async getUserMeals(userId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/admin/users/${userId}/meals${query ? `?${query}` : ''}`);
  }

  /**
   * Obtém detalhes de uma refeição específica
   */
  async getUserMeal(userId, mealId) {
    return this.request(`/admin/users/${userId}/meals/${mealId}`);
  }

  /**
   * Atualiza uma refeição do usuário
   */
  async updateUserMeal(userId, mealId, data) {
    return this.request(`/admin/users/${userId}/meals/${mealId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  /**
   * Exclui uma refeição do usuário
   */
  async deleteUserMeal(userId, mealId) {
    return this.request(`/admin/users/${userId}/meals/${mealId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Lista ingredientes de uma refeição
   */
  async getMealIngredients(userId, mealId) {
    return this.request(`/admin/users/${userId}/meals/${mealId}/ingredients`);
  }

  /**
   * Atualiza um ingrediente de uma refeição
   */
  async updateMealIngredient(userId, mealId, ingredientId, data) {
    return this.request(`/admin/users/${userId}/meals/${mealId}/ingredients/${ingredientId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  /**
   * Exclui um ingrediente de uma refeição
   */
  async deleteMealIngredient(userId, mealId, ingredientId) {
    return this.request(`/admin/users/${userId}/meals/${mealId}/ingredients/${ingredientId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Obtém histórico de consumo de um usuário
   */
  async getUserConsumptionHistory(userId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/admin/users/${userId}/consumption-history${query ? `?${query}` : ''}`);
  }

  /**
   * Obtém estatísticas gerais de refeições
   */
  async getMealsStats() {
    return this.request('/admin/meals/stats');
  }

  // ==================== ACTIVITIES ====================

  /**
   * Lista todas as atividades
   */
  async getActivities() {
    return this.request('/activities');
  }

  /**
   * Cria uma nova atividade
   */
  async createActivity(data) {
    return this.request('/activities', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Atualiza uma atividade
   */
  async updateActivity(id, data) {
    return this.request(`/activities/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  /**
   * Exclui uma atividade
   */
  async deleteActivity(id) {
    return this.request(`/activities/${id}`, {
      method: 'DELETE'
    });
  }

  // ==================== DIVISIONS ====================

  /**
   * Lista divisões de uma atividade
   */
  async getDivisions(activityId) {
    return this.request(`/activities/${activityId}/divisions`);
  }

  /**
   * Cria uma nova divisão
   */
  async createDivision(activityId, data) {
    return this.request(`/activities/${activityId}/divisions`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Atualiza uma divisão
   */
  async updateDivision(activityId, divisionId, data) {
    return this.request(`/activities/${activityId}/divisions/${divisionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  /**
   * Exclui uma divisão
   */
  async deleteDivision(activityId, divisionId) {
    return this.request(`/activities/${activityId}/divisions/${divisionId}`, {
      method: 'DELETE'
    });
  }

  // ==================== EXERCISES ====================

  /**
   * Lista exercícios de uma divisão
   */
  async getExercises(activityId, divisionId) {
    return this.request(`/activities/${activityId}/divisions/${divisionId}/exercises`);
  }

  /**
   * Cria um novo exercício
   */
  async createExercise(activityId, divisionId, data) {
    return this.request(`/activities/${activityId}/divisions/${divisionId}/exercises`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Atualiza um exercício
   */
  async updateExercise(activityId, divisionId, exerciseId, data) {
    return this.request(`/activities/${activityId}/divisions/${divisionId}/exercises/${exerciseId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  /**
   * Exclui um exercício
   */
  async deleteExercise(activityId, divisionId, exerciseId) {
    return this.request(`/activities/${activityId}/divisions/${divisionId}/exercises/${exerciseId}`, {
      method: 'DELETE'
    });
  }

  // ==================== EXERCISES BULK OPERATIONS ====================

  /**
   * Obtém status dos exercícios (vinculados vs disponíveis)
   */
  async getExercisesBulkStatus(activityId, divisionId) {
    return this.request(`/activities/${activityId}/divisions/${divisionId}/exercises/bulk-status`);
  }

  /**
   * Vincula exercícios a uma divisão
   */
  async bulkAssignExercises(activityId, divisionId, exerciseIds) {
    return this.request(`/activities/${activityId}/divisions/${divisionId}/exercises/bulk-assign`, {
      method: 'PATCH',
      body: JSON.stringify({ exercise_ids: exerciseIds })
    });
  }

  /**
   * Desvincula exercícios de uma divisão
   */
  async bulkDeleteExercises(activityId, divisionId, exerciseIds) {
    return this.request(`/activities/${activityId}/divisions/${divisionId}/exercises/bulk-delete`, {
      method: 'DELETE',
      body: JSON.stringify({ exercise_ids: exerciseIds })
    });
  }

  /**
   * Copia exercícios para outra divisão
   */
  async bulkCopyExercises(activityId, targetDivisionId, sourceDivisionId, exerciseIds) {
    return this.request(`/activities/${activityId}/divisions/${targetDivisionId}/exercises/bulk-copy`, {
      method: 'POST',
      body: JSON.stringify({
        source_division_id: sourceDivisionId,
        target_division_id: targetDivisionId,
        exercise_ids: exerciseIds
      })
    });
  }

  // ==================== APP VERSIONS ====================

  /**
   * Lista todas as versões do app (iOS e Android)
   */
  async getAppVersions() {
    return this.request('/admin/app-versions');
  }

  /**
   * Obtém detalhes da versão de uma plataforma
   * @param {string} platform - 'ios' ou 'android'
   */
  async getAppVersion(platform) {
    return this.request(`/admin/app-versions/${platform}`);
  }

  /**
   * Atualiza a versão de uma plataforma
   * @param {string} platform - 'ios' ou 'android'
   * @param {object} data - Dados da versão
   */
  async updateAppVersion(platform, data) {
    return this.request(`/admin/app-versions/${platform}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  /**
   * Obtém histórico de alterações de versão
   * @param {object} params - Parâmetros de filtro (platform, limit)
   */
  async getAppVersionsHistory(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/admin/app-versions/history${query ? `?${query}` : ''}`);
  }

  // ==================== NOTIFICATIONS ====================

  /**
   * Lista filtros disponíveis para seleção de destinatários
   */
  async getNotificationFilters() {
    return this.request('/admin/notifications/filters');
  }

  /**
   * Conta quantos destinatários receberão a notificação
   */
  async countNotificationRecipients(recipients) {
    return this.request('/admin/notifications/count-recipients', {
      method: 'POST',
      body: JSON.stringify({ recipients })
    });
  }

  /**
   * Envia email de notificação/marketing
   */
  async sendNotificationEmail(data) {
    return this.request('/admin/notifications/send-email', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Lista histórico de campanhas enviadas
   */
  async getNotificationHistory(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page);
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.date_from) searchParams.append('date_from', params.date_from);
    if (params.date_to) searchParams.append('date_to', params.date_to);
    
    const queryString = searchParams.toString();
    return this.request(`/admin/notifications/history${queryString ? `?${queryString}` : ''}`);
  }

  // ==================== PUSH APP (ADMIN) ====================

  async getPushNavigationActions() {
    return this.request('/admin/push/navigation-actions');
  }

  async getPushFilters() {
    return this.request('/admin/push/filters');
  }

  async countPushRecipients(recipients) {
    return this.request('/admin/push/count-recipients', {
      method: 'POST',
      body: JSON.stringify({ recipients })
    });
  }

  async sendPushBroadcast(data) {
    return this.request('/admin/push/broadcast', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getPushCampaignHistory(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page);
    if (params.limit) searchParams.append('limit', params.limit);
    const queryString = searchParams.toString();
    return this.request(`/admin/push/campaigns${queryString ? `?${queryString}` : ''}`);
  }

  async getPushCampaignDetail(campaignId) {
    return this.request(`/admin/push/campaigns/${campaignId}`);
  }

  async getPushCampaignDeliveries(campaignId, params = {}) {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page);
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.status) searchParams.append('status', params.status);
    const queryString = searchParams.toString();
    return this.request(
      `/admin/push/campaigns/${campaignId}/deliveries${queryString ? `?${queryString}` : ''}`
    );
  }

  // ==================== SURVEYS (PESQUISAS) ====================

  async getSurveys(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page);
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.status) searchParams.append('status', params.status);
    const queryString = searchParams.toString();
    return this.request(`/admin/surveys${queryString ? `?${queryString}` : ''}`);
  }

  async getSurvey(surveyId) {
    return this.request(`/admin/surveys/${surveyId}`);
  }

  async createSurvey(data) {
    return this.request('/admin/surveys', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSurvey(surveyId, data) {
    return this.request(`/admin/surveys/${surveyId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSurvey(surveyId) {
    return this.request(`/admin/surveys/${surveyId}`, { method: 'DELETE' });
  }

  async countSurveyRecipients(recipients) {
    return this.request('/admin/surveys/count-recipients', {
      method: 'POST',
      body: JSON.stringify({ recipients }),
    });
  }

  async publishSurvey(surveyId, data) {
    return this.request(`/admin/surveys/${surveyId}/publish`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async closeSurvey(surveyId) {
    return this.request(`/admin/surveys/${surveyId}/close`, { method: 'POST' });
  }

  async getSurveyAnalytics(surveyId) {
    return this.request(`/admin/surveys/${surveyId}/analytics`);
  }
}

// Exporta instância única (singleton)
export const adminAPI = new AdminAPI();
export default adminAPI;

